"use client";

import { c as _c } from "react/compiler-runtime";
import { startTransition, useEffect, useRef, useState } from "react";
import { HEADER_HEIGHT } from "@/lib/constants/layout";
export type ScrollDirection = "up" | "down" | null;
interface UseScrollDirectionOptions {
  /** Minimum scroll delta to trigger direction change (prevents jitter) */
  threshold?: number;
  /** Scroll position threshold before hide/show behavior kicks in (defaults to header height) */
  offsetThreshold?: number;
  /** Initial direction before any scroll */
  initialDirection?: ScrollDirection;
}
interface UseScrollDirectionResult {
  /** Current scroll direction */
  direction: ScrollDirection;
  /** Whether we've scrolled past the offset threshold */
  isPastThreshold: boolean;
}

/**
 * Hook that tracks scroll direction with hysteresis to prevent jitter.
 * Also tracks whether we've scrolled past an offset threshold.
 */
export function useScrollDirection(t0) {
  const $ = _c(9);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    threshold: t2,
    offsetThreshold: t3,
    initialDirection: t4
  } = t1;
  const threshold = t2 === undefined ? 10 : t2;
  const offsetThreshold = t3 === undefined ? HEADER_HEIGHT : t3;
  const initialDirection = t4 === undefined ? null : t4;
  const [direction, setDirection] = useState(initialDirection);
  const [isPastThreshold, setIsPastThreshold] = useState(false);
  const lastScrollY = useRef(0);
  const lastDirection = useRef(initialDirection);
  const lastPastThreshold = useRef(false);
  let t5;
  let t6;
  if ($[2] !== offsetThreshold || $[3] !== threshold) {
    t5 = () => {
      const initialScrollY = window.scrollY;
      lastScrollY.current = initialScrollY;
      const initialPastThreshold = initialScrollY > offsetThreshold;
      lastPastThreshold.current = initialPastThreshold;
      startTransition(() => setIsPastThreshold(initialPastThreshold));
      const handleScroll = () => {
        const currentScrollY = window.scrollY;
        const delta = currentScrollY - lastScrollY.current;
        const nextPastThreshold = currentScrollY > offsetThreshold;
        if (nextPastThreshold !== lastPastThreshold.current) {
          lastPastThreshold.current = nextPastThreshold;
          startTransition(() => setIsPastThreshold(nextPastThreshold));
        }
        if (currentScrollY <= 0) {
          if (lastDirection.current !== "up") {
            lastDirection.current = "up";
            startTransition(() => setDirection("up"));
          }
          lastScrollY.current = currentScrollY;
          return;
        }
        if (Math.abs(delta) >= threshold) {
          const newDirection = delta > 0 ? "down" : "up";
          if (newDirection !== lastDirection.current) {
            lastDirection.current = newDirection;
            startTransition(() => setDirection(newDirection));
          }
          lastScrollY.current = currentScrollY;
        }
      };
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    };
    t6 = [threshold, offsetThreshold];
    $[2] = offsetThreshold;
    $[3] = threshold;
    $[4] = t5;
    $[5] = t6;
  } else {
    t5 = $[4];
    t6 = $[5];
  }
  useEffect(t5, t6);
  let t7;
  if ($[6] !== direction || $[7] !== isPastThreshold) {
    t7 = {
      direction,
      isPastThreshold
    };
    $[6] = direction;
    $[7] = isPastThreshold;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  return t7;
}